import React, { useMemo, useState } from "react";
import type { PricePlanRow } from "../../../services/PricePlanService";
import {
	BIG_CHANGE_PCT,
	ChangeFilter,
	EMPTY_FILTER,
	RowFilter,
	SortDir,
	SortKey,
	fmtPct,
	fmtUsd,
	groupRows,
	rowMatches,
	uniqueValues,
} from "./pricePlanUtils";
import { GROUP_TONE, cell, headCell, input, muted, num } from "./pricePlanStyles";

/**
 * Bảng plan đang chờ: lọc + sắp xếp, nhóm country → city → zone.
 * Bọc trong khung cuộn ngang để xem được trên điện thoại.
 */

const COLS = 10;

const GroupBadge: React.FC<{ group: string | null }> = ({ group }) => {
	if (!group) return <span style={muted}>—</span>;
	const t = GROUP_TONE[group] || { bg: "#f2f4f7", fg: "#344054" };
	return (
		<span
			style={{
				background: t.bg,
				color: t.fg,
				borderRadius: 6,
				padding: "1px 8px",
				fontSize: 12,
				fontWeight: 700,
			}}>
			{group}
		</span>
	);
};

const pctColor = (v: number | null) => {
	if (v == null || v === 0) return "#475467";
	if (Math.abs(v) > BIG_CHANGE_PCT) return "#b42318";
	return v > 0 ? "#027a48" : "#b54708";
};

export default function PlanTable({ rows }: { rows: PricePlanRow[] }) {
	const [filter, setFilter] = useState<RowFilter>(EMPTY_FILTER);
	const [sortKey, setSortKey] = useState<SortKey>("station");
	const [sortDir, setSortDir] = useState<SortDir>("asc");

	const countries = useMemo(() => uniqueValues(rows, (r) => r.country), [rows]);
	const groupsList = useMemo(() => uniqueValues(rows, (r) => r.group), [rows]);
	const filtered = useMemo(() => rows.filter((r) => rowMatches(r, filter)), [rows, filter]);
	const grouped = useMemo(() => groupRows(filtered, sortKey, sortDir), [filtered, sortKey, sortDir]);

	const sortBy = (k: SortKey) => {
		if (k === sortKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
		else {
			setSortKey(k);
			setSortDir(k === "changePct" ? "desc" : "asc");
		}
	};
	const sortTh = (k: SortKey, label: string, right = false) => (
		<th
			key={k}
			style={{ ...headCell, ...(right ? num : {}), cursor: "pointer", userSelect: "none" }}
			onClick={() => sortBy(k)}
			aria-sort={sortKey === k ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
			{label}
			{sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
		</th>
	);

	return (
		<div>
			<div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "0 0 10px" }}>
				<input
					placeholder="Search station, city, zone, note…"
					aria-label="Search rows"
					value={filter.search}
					onChange={(e) => setFilter({ ...filter, search: e.target.value })}
					style={{ ...input, flex: "1 1 220px", minWidth: 0 }}
				/>
				<select
					aria-label="Filter by country"
					value={filter.country}
					onChange={(e) => setFilter({ ...filter, country: e.target.value })}
					style={input}>
					<option value="">All countries</option>
					{countries.map((c) => (
						<option
							key={c}
							value={c}>
							{c}
						</option>
					))}
				</select>
				<select
					aria-label="Filter by group"
					value={filter.group}
					onChange={(e) => setFilter({ ...filter, group: e.target.value })}
					style={input}>
					<option value="">All groups</option>
					{groupsList.map((g) => (
						<option
							key={g}
							value={g}>
							{g}
						</option>
					))}
				</select>
				<select
					aria-label="Filter by change"
					value={filter.change}
					onChange={(e) => setFilter({ ...filter, change: e.target.value as ChangeFilter })}
					style={input}>
					<option value="all">All changes</option>
					<option value="up">Price up</option>
					<option value="down">Price down</option>
					<option value="same">No change</option>
					<option value="big">Change &gt; {BIG_CHANGE_PCT}%</option>
				</select>
			</div>
			<div style={{ ...muted, margin: "0 0 6px" }}>
				Showing {filtered.length} of {rows.length} stations · prices are all-in USD
			</div>
			<div
				style={{
					overflowX: "auto",
					maxHeight: "70vh",
					overflowY: "auto",
					border: "1px solid #eaecf0",
					borderRadius: 8,
					WebkitOverflowScrolling: "touch",
				}}>
				<table style={{ borderCollapse: "collapse", width: "100%", minWidth: 1180, fontSize: 13 }}>
					<thead>
						<tr>
							{sortTh("station", "Station")}
							{sortTh("group", "Group")}
							<th style={{ ...headCell, ...num }}>Current small</th>
							{sortTh("currentStandard", "Current standard", true)}
							<th style={{ ...headCell, ...num }}>Current large</th>
							<th style={{ ...headCell, ...num }}>New small</th>
							{sortTh("nextStandard", "New standard", true)}
							<th style={{ ...headCell, ...num }}>New large</th>
							{sortTh("changePct", "Δ standard %", true)}
							<th style={headCell}>Note</th>
						</tr>
					</thead>
					<tbody>
						{grouped.length === 0 ? (
							<tr>
								<td
									colSpan={COLS}
									style={{ ...cell, textAlign: "center", color: "#667085", padding: 20 }}>
									No stations match the filters.
								</td>
							</tr>
						) : null}
						{grouped.map((cg) => (
							<React.Fragment key={cg.key}>
								<tr>
									<td
										colSpan={COLS}
										style={{ ...cell, background: "#eef4ff", fontWeight: 700, color: "#1d2939" }}>
										{cg.country} <span style={muted}>· {cg.count}</span>
									</td>
								</tr>
								{cg.cities.map((ci) => (
									<React.Fragment key={ci.key}>
										<tr>
											<td
												colSpan={COLS}
												style={{
													...cell,
													background: "#f8f9fc",
													fontWeight: 600,
													paddingLeft: 20,
												}}>
												{ci.city} <span style={muted}>· {ci.count}</span>
											</td>
										</tr>
										{ci.zones.map((z) => (
											<React.Fragment key={z.key}>
												<tr>
													<td
														colSpan={COLS}
														style={{
															...cell,
															color: "#475467",
															fontSize: 12,
															paddingLeft: 34,
														}}>
														{z.zoneName} <span style={muted}>· {z.rows.length}</span>
													</td>
												</tr>
												{z.rows.map((r) => (
													<tr
														key={r.partnerId}
														data-testid={`plan-row-${r.partnerId}`}>
														<td
															style={{
																...cell,
																paddingLeft: 46,
																whiteSpace: "normal",
																minWidth: 200,
															}}>
															<span style={{ color: "#667085" }}>#{r.partnerId}</span>{" "}
															{r.partnerName}
														</td>
														<td style={cell}>
															<GroupBadge group={r.group} />
														</td>
														<td style={{ ...cell, ...num }}>{fmtUsd(r.current?.small)}</td>
														<td style={{ ...cell, ...num }}>
															{fmtUsd(r.current?.standard)}
														</td>
														<td style={{ ...cell, ...num }}>{fmtUsd(r.current?.large)}</td>
														<td style={{ ...cell, ...num, fontWeight: 600 }}>
															{fmtUsd(r.next?.small)}
														</td>
														<td style={{ ...cell, ...num, fontWeight: 600 }}>
															{fmtUsd(r.next?.standard)}
														</td>
														<td style={{ ...cell, ...num, fontWeight: 600 }}>
															{fmtUsd(r.next?.large)}
														</td>
														<td
															style={{
																...cell,
																...num,
																fontWeight: 700,
																color: pctColor(r.changePct),
															}}>
															{fmtPct(r.changePct)}
														</td>
														<td
															style={{
																...cell,
																whiteSpace: "normal",
																minWidth: 180,
																color: "#475467",
															}}>
															{r.note || ""}
														</td>
													</tr>
												))}
											</React.Fragment>
										))}
									</React.Fragment>
								))}
							</React.Fragment>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}
